import React, { useState, useEffect } from "react";
import { rqx } from "../../../../../core/request/API";
import { env } from "../../../../../core/Env";
import { useDebounce } from "../../../../../core/hooks/useDebounce";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  InputAdornment,
  Radio,
  CircularProgress,
} from "@mui/material";
import { SearchIcons } from "../../../../../core/global/Icons";
import Button from "../../../../../core/ui/Button";

const ClientSelectionModal = ({ open, onClose, onConfirm, companyId, selectedClient = null }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selected, setSelected] = useState(selectedClient);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const debouncedSearch = useDebounce(searchQuery, 500);

  useEffect(() => {
    if (!open || !companyId) return;
    let isMounted = true;
    setLoading(true);
    const qsp = { company_id: companyId };
    // const qsp = { company_id: 1 };
    if (debouncedSearch.trim()) qsp.q = debouncedSearch.trim();
    rqx
      .get(`<<tool>>/${env()}/clients/read`, qsp)
      .then((result) => {
        if (isMounted) setClients(Array.isArray(result) ? result : result.data || []);
      })
      .catch(() => {
        if (isMounted) setClients([]);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [open, companyId, debouncedSearch]);

  useEffect(() => {
    if (open) {
      setSelected(selectedClient);
    }
  }, [open, selectedClient]);

  const handleSelect = (client) => {
    setSelected(client.id === selected?.id ? null : client);
  };

  const handleConfirm = () => {
    if (selected) {
      onConfirm(selected);
      handleClose();
    }
  };

  const handleClose = () => {
    setSearchQuery("");
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          borderRadius: "12px",
          overflow: "hidden",
          padding: "24px",
          maxWidth: "1080px",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontSize: "16px",
          fontWeight: 600,
          lineHeight: "24px",
          color: "#292C2F",
          padding: 0,
          marginBottom: "24px",
        }}
      >
        Existing Clients
      </DialogTitle>
      <DialogContent
        sx={{ padding: 0, marginBottom: "24px", overflow: "hidden" }}
      >
        <TextField
          sx={{ width: "456px" }}
          variant="outlined"
          placeholder="Search Client"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcons sx={{ color: "#283745", width: "20px", height: "20px" }} />
              </InputAdornment>
            ),
            sx: {
              height: "40px",
              backgroundColor: "#F7F8F8",
              padding: "8px 12px",
              borderRadius: "6px",
              fontFamily: "Inter",
              fontSize: "14px",
              fontWeight: 400,
              lineHeight: "24px",
              "& fieldset": {
                border: "none",
              },
              "& input": {
                padding: 0,
                paddingLeft: "12px",
                color: "#292C2F",
              },
              "& input::placeholder": {
                fontFamily: "Inter",
                fontSize: "14px",
                fontWeight: 400,
                lineHeight: "24px",
                color: "#8F969D",
                opacity: 1,
              },
            },
          }}
        />

        <Box
          sx={{
            maxHeight: "350px",
            overflowY: "auto",
            marginTop: "24px",
            paddingRight: "5px",
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#D6D9DB",
              borderRadius: "3px",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",
            }}
          >
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={28} sx={{ color: "#003F2E" }} />
              </Box>
            ) : clients.length > 0 ? (
              clients.map((client, idx) => (
                <Box
                  key={client.id}
                  onClick={() => handleSelect(client)}
                  sx={{
                    padding: "16px",
                    borderRadius: "12px",
                    border:
                      selected?.id === client.id
                        ? "2px solid #11783C"
                        : "1px solid #D6D9DB",
                    backgroundColor:
                      selected?.id === client.id ? "#F0F9F4" : "#FFFFFF",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      borderColor: "#11783C",
                      backgroundColor: "#F0F9F4",
                    },
                  }}
                >
                  <Radio
                    checked={selected?.id === client.id}
                    onChange={() => handleSelect(client)}
                    sx={{
                      padding: 0,
                      color: "#D6D9DB",
                      "&.Mui-checked": {
                        color: "#003F2E",
                      },
                      "& .MuiSvgIcon-root": {
                        fontSize: "20px",
                      },
                    }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      flex: 1,
                      gap: "4px",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Inter",
                        fontSize: "16px",
                        fontWeight: 600,
                        lineHeight: "24px",
                        color: "#292C2F",
                      }}
                    >
                      {client.business_name || client.name || client.client_name || "Unnamed"}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: "Inter",
                        fontSize: "14px",
                        fontWeight: 400,
                        lineHeight: "24px",
                        color: "#5B6167",
                      }}
                    >
                      Client No. - {client.client_num || client.clientNo || client.client_no || "-"}
                    </Typography>
                  </Box>
                </Box>
              ))
            ) : (
              <Box sx={{ textAlign: "center", py: 3 }}>
                <Typography
                  sx={{
                    color: "#8F969D",
                    fontSize: "14px",
                    fontFamily: "Inter",
                  }}
                >
                  No clients found
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: 0, gap: "12px" }}>
        <Button
          onClick={handleConfirm}
          disabled={!selected}
          variant="dark"
          sx={{
            height: "52px",
            fontSize: "14px",
            fontWeight: 600,
            lineHeight: "24px",
            fontFamily: "Inter",
          }}
        >
          Confirm
        </Button>
        <Button
          onClick={handleClose}
          variant="neutral"
          sx={{
            height: "52px",
            fontSize: "14px",
            fontWeight: 600,
            lineHeight: "24px",
            fontFamily: "Inter",
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClientSelectionModal;
